import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import logoDark from './img/pexa-logo-white.png';
import logoLight from './img/pexa-logo.png';

function getUserDisplayName(u) {
  if (!u) return 'User';
  return (
    u.preferred_name ||
    u.display_name ||
    u.displayName ||
    u.full_name ||
    u.name ||
    u.preferred_username ||
    u.given_name ||
    u.nickname ||
    u.email ||
    'User'
  );
}

export default function Navbar({ isDark, onThemeToggle, actions, onHomeClick }) {
  const { user, isLoading, login, logout } = useAuth();
  const location = useLocation();

  const navLinks = [
    { to: '/', label: 'Database', icon: 'dataset' },
    { to: '/about', label: 'About', icon: 'info' },
    { to: '/help', label: 'Help', icon: 'help' },
  ];

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const handleHomeLinkClick = (to) => (event) => {
    if (!onHomeClick) return;
    if (to !== '/') return;
    if (location.pathname !== '/') return;
    if (event.defaultPrevented) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onHomeClick();
  };

  // Navigating to a primary section (Database/About/Help or the brand logo)
  // means leaving the study section, so drop any persisted Studies edit mode.
  const handleNavAway = (to) => (event) => {
    try { sessionStorage.removeItem('studiesEditMode'); } catch { /* ignore */ }
    handleHomeLinkClick(to)(event);
  };

  return (
    <nav className={`site-nav${isDark ? ' site-nav-dark' : ' site-nav-light'}`} aria-label="Main navigation">
      <div className="site-nav-brand">
        <Link to="/" aria-label="Small Airway Atlas – Home" onClick={handleNavAway('/')}>
          <img
            src={isDark ? logoDark : logoLight}
            alt="PExA logo"
            className="site-nav-logo"
          />
          <span className="site-nav-title">Small Airway Atlas<span aria-hidden="true">™</span></span>
        </Link>
      </div>

      <ul className="site-nav-links" role="list">
        {navLinks.map(({ to, label, icon }) => (
          <li key={to}>
            <Link
              to={to}
              className={`site-nav-link${isActive(to) ? ' site-nav-link-active' : ''}`}
              onClick={handleNavAway(to)}
            >
              <span className="material-symbols-outlined site-nav-icon" aria-hidden="true">{icon}</span>
              {label}
            </Link>
          </li>
        ))}
      </ul>

      <div className="site-nav-controls">
        {actions}
        <button
          type="button"
          className="icon-btn"
          onClick={onThemeToggle}
          aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          aria-pressed={isDark}
          title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        >
          <span className="material-symbols-outlined theme-icon" aria-hidden="true">
            {isDark ? 'light_mode' : 'dark_mode'}
          </span>
        </button>

        {isLoading ? (
          <span style={{ fontSize: 14, opacity: 0.7 }}>Loading…</span>
        ) : user ? (
          <>
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 20, verticalAlign: 'middle' }}>account_circle</span>
            <span className="user-name site-nav-username" title={user.email || ''}>
              {getUserDisplayName(user)}
            </span>
            {user.email && (
              <span className="user-email site-nav-useremail">({user.email})</span>
            )}
            <button type="button" className="pill-btn" onClick={() => logout()} aria-label="Log out">
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 18, verticalAlign: 'middle' }}>logout</span>
              {' '}Logout
            </button>
          </>
        ) : (
          <button type="button" className="pill-btn pill-btn-primary" onClick={() => login()} aria-label="Log in">
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 18, verticalAlign: 'middle' }}>login</span>
            {' '}Login
          </button>
        )}
      </div>
    </nav>
  );
}
